/* ============================================================
   CUSTOM SECTIONS — Shared JavaScript
   Handles carousels, mobile menu, Slide-Over Cart Drawer
   ============================================================ */

(function () {
  'use strict';

  /* ----------------------------------------------------------
     ACCOUNT DROPDOWN CONTROLLER
     ---------------------------------------------------------- */
  const AccountDropdown = {
    wrapper: null,
    trigger: null,
    panel: null,
    isOpen: false,

    init() {
      this.wrapper = document.querySelector('[data-account-dropdown-wrapper]');
      if (!this.wrapper) return;

      this.trigger = this.wrapper.querySelector('[data-account-dropdown-trigger]');
      this.panel = this.wrapper.querySelector('[data-account-dropdown-panel]');

      if (this.trigger) {
        this.trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggle();
        });
      }

      // Close on click outside
      document.addEventListener('click', (e) => {
        if (this.isOpen && this.wrapper && !this.wrapper.contains(e.target)) {
          this.close();
        }
      });

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    },

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    },

    open() {
      // Close Cart Drawer if open
      if (CartDrawer && CartDrawer.isOpen) {
        CartDrawer.close();
      }
      if (this.wrapper) this.wrapper.classList.add('is-open');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
    },

    close() {
      if (this.wrapper) this.wrapper.classList.remove('is-open');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      this.isOpen = false;
    }
  };

  /* ----------------------------------------------------------
     CART DRAWER CONTROLLER
     ---------------------------------------------------------- */
  const CartDrawer = {
    drawer: null,
    backdrop: null,
    isOpen: false,

    init() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.backdrop = document.querySelector('#custom-cart-drawer-backdrop');
      if (!this.drawer) return;

      // Triggers to open
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cart-drawer-trigger]');
        if (trigger) {
          e.preventDefault();
          this.open();
        }
      });

      // Direct backdrop click to close
      if (this.backdrop) {
        this.backdrop.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.close();
        });
      }

      // Close buttons ([X] button, browse button, etc.)
      document.addEventListener('click', (e) => {
        const closeBtn = e.target.closest('[data-cart-drawer-close]');
        if (closeBtn) {
          e.preventDefault();
          this.close();
          return;
        }

        // Click outside drawer panel when drawer is open
        if (this.isOpen && this.drawer && !this.drawer.contains(e.target) && !e.target.closest('[data-cart-drawer-trigger]')) {
          this.close();
        }
      });

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Qty change and item delete inside drawer
      this.drawer.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('[data-drawer-qty-change]');
        if (qtyBtn) {
          e.preventDefault();
          const key = qtyBtn.dataset.key;
          const currentQty = parseInt(qtyBtn.dataset.qty, 10);
          const change = parseInt(qtyBtn.dataset.drawerQtyChange, 10);
          const newQty = Math.max(0, currentQty + change);
          this.updateItem(key, newQty);
          return;
        }

        const deleteBtn = e.target.closest('[data-drawer-item-remove]');
        if (deleteBtn) {
          e.preventDefault();
          const key = deleteBtn.dataset.key;
          this.updateItem(key, 0);
          return;
        }
      });
    },

    open() {
      if (!this.drawer) return;
      // Close Account Dropdown if open
      if (typeof AccountDropdown !== 'undefined' && AccountDropdown.isOpen) {
        AccountDropdown.close();
      }
      this.fetchAndUpdate();
      this.drawer.classList.add('is-open', 'is-active');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.backdrop) this.backdrop.classList.add('is-open', 'is-active');
      document.body.classList.add('cart-drawer-open');
      document.body.style.overflow = 'hidden';
      this.isOpen = true;
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open', 'is-active');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.backdrop) this.backdrop.classList.remove('is-open', 'is-active');
      document.body.classList.remove('cart-drawer-open');
      document.body.style.overflow = '';
      this.isOpen = false;
    },

    updateItem(key, quantity) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then((r) => r.json())
        .then((cart) => {
          this.render(cart);
        })
        .catch((err) => console.error('Cart update failed', err));
    },

    fetchAndUpdate() {
      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          this.render(cart);
        })
        .catch((err) => console.error('Cart fetch failed', err));
    },

    render(cart) {
      // Update global badges
      document.querySelectorAll('[data-cart-count], [data-drawer-count]').forEach((el) => {
        el.textContent = cart.item_count;
      });

      const emptyEl = this.drawer.querySelector('[data-drawer-empty]');
      const itemsWrap = this.drawer.querySelector('[data-drawer-items-wrap]');
      const footerEl = this.drawer.querySelector('[data-drawer-footer]');
      const shippingBar = this.drawer.querySelector('[data-drawer-shipping-bar]');

      if (cart.item_count === 0) {
        if (emptyEl) emptyEl.style.display = 'flex';
        if (itemsWrap) itemsWrap.style.display = 'none';
        if (footerEl) footerEl.style.display = 'none';
        if (shippingBar) shippingBar.style.display = 'none';
        return;
      }

      if (emptyEl) emptyEl.style.display = 'none';
      if (itemsWrap) itemsWrap.style.display = 'block';
      if (footerEl) footerEl.style.display = 'block';
      if (shippingBar) shippingBar.style.display = 'block';

      // Render items
      const listEl = this.drawer.querySelector('[data-drawer-items]');
      if (listEl) {
        let itemsHtml = '';
        cart.items.forEach((item) => {
          const itemPrice = Math.round(item.price / 100);
          const comparePrice = Math.round(itemPrice * 1.4);
          const discountPct = 28;

          itemsHtml += `
            <div class="custom-cart-drawer__item" data-line-key="${item.key}">
              <div class="custom-cart-drawer__item-img">
                ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy" width="70" height="70">` : `<div style="width:70px;height:70px;background:#F8FAFC;border-radius:12px;display:flex;align-items:center;justify-content:center;">🧸</div>`}
              </div>
              <div class="custom-cart-drawer__item-info">
                <div class="custom-cart-drawer__item-top">
                  <h4 class="custom-cart-drawer__item-title">${item.product_title || item.title}</h4>
                  <div class="custom-cart-drawer__item-price-main">₹${Math.round(item.final_line_price / 100)}</div>
                </div>
                <div class="custom-cart-drawer__item-pricing">
                  <span class="custom-cart-drawer__item-unit-price">₹${itemPrice}</span>
                  <span class="custom-cart-drawer__item-compare">₹${comparePrice}</span>
                  <span class="custom-cart-drawer__item-discount">${discountPct}% OFF</span>
                </div>
                <div class="custom-cart-drawer__item-controls">
                  <div class="custom-cart-drawer__qty-pill">
                    <button type="button" class="custom-cart-drawer__qty-btn" data-drawer-qty-change="-1" data-key="${item.key}" data-qty="${item.quantity}">−</button>
                    <span class="custom-cart-drawer__qty-val">${item.quantity}</span>
                    <button type="button" class="custom-cart-drawer__qty-btn" data-drawer-qty-change="1" data-key="${item.key}" data-qty="${item.quantity}">+</button>
                  </div>
                  <button type="button" class="custom-cart-drawer__item-delete" data-drawer-item-remove data-key="${item.key}" aria-label="Remove item">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="18" height="18">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          `;
        });
        listEl.innerHTML = itemsHtml;
      }

      // Free Gift Tier progress
      const giftThreshold = 999;
      const totalRupees = Math.round(cart.total_price / 100);
      const giftDiff = Math.max(0, giftThreshold - totalRupees);
      const giftPct = Math.min(100, Math.round((totalRupees / giftThreshold) * 100));

      const giftTitleEl = this.drawer.querySelector('[data-drawer-gift-title]');
      const giftFillEl = this.drawer.querySelector('[data-drawer-gift-fill]');

      if (giftTitleEl) {
        if (totalRupees >= giftThreshold) {
          giftTitleEl.innerHTML = `🎉 <strong>You unlocked a FREE gift!</strong>`;
        } else {
          giftTitleEl.innerHTML = `You're <strong style="color: #EA580C;">₹${giftDiff}</strong> away from a FREE gift!`;
        }
      }
      if (giftFillEl) {
        giftFillEl.style.width = `${giftPct}%`;
      }

      // Totals & Savings
      const subtotalEl = this.drawer.querySelector('[data-drawer-subtotal]');
      const totalEl = this.drawer.querySelector('[data-drawer-total]');
      const savingsEl = this.drawer.querySelector('[data-drawer-total-savings]');

      if (subtotalEl) subtotalEl.textContent = `₹${totalRupees}`;
      if (totalEl) totalEl.textContent = `₹${totalRupees}`;
      if (savingsEl) {
        const estimatedSavings = Math.max(150, Math.round(totalRupees * 0.35));
        savingsEl.textContent = `₹${estimatedSavings}`;
      }
    }
  };

  /* ----------------------------------------------------------
     CAROUSEL — generic horizontal scroll with arrows, progress + autoplay
     ---------------------------------------------------------- */
  class CustomCarousel {
    constructor(el) {
      this.container = el;
      this.track = el.querySelector('[data-carousel-track]');
      if (!this.track) return;

      const parentSection = el.closest('section') || el.parentElement;
      this.prevBtn = parentSection ? parentSection.querySelector('[data-carousel-prev]') : el.querySelector('[data-carousel-prev]');
      this.nextBtn = parentSection ? parentSection.querySelector('[data-carousel-next]') : el.querySelector('[data-carousel-next]');
      this.progressFill = el.querySelector('[data-carousel-progress-fill]');

      this.scrollAmount = 280;
      this.autoplayTimer = null;

      this.prevBtn && this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.scroll(-1);
      });
      this.nextBtn && this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.scroll(1);
      });
      this.track.addEventListener('scroll', () => this.updateProgress(), { passive: true });

      // Autoplay: automatically cycles categories every 3.5s
      this.startAutoplay();
      this.container.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.container.addEventListener('mouseleave', () => this.startAutoplay());
      this.container.addEventListener('touchstart', () => this.pauseAutoplay(), { passive: true });
      this.container.addEventListener('touchend', () => this.startAutoplay(), { passive: true });

      requestAnimationFrame(() => this.updateProgress());
    }

    scroll(direction) {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const maxScroll = scrollWidth - clientWidth;
      let newScroll = scrollLeft + direction * this.scrollAmount;

      if (newScroll > maxScroll + 40) {
        newScroll = 0;
      } else if (newScroll < -40) {
        newScroll = maxScroll;
      }

      this.track.scrollTo({ left: newScroll, behavior: 'smooth' });
    }

    startAutoplay() {
      if (this.autoplayTimer) clearInterval(this.autoplayTimer);
      this.autoplayTimer = setInterval(() => {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const maxScroll = scrollWidth - clientWidth;
        if (maxScroll <= 0) return;

        if (scrollLeft >= maxScroll - 15) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.scroll(1);
        }
      }, 3500);
    }

    pauseAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    updateProgress() {
      if (!this.progressFill) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const maxScroll = scrollWidth - clientWidth;
      const pct = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
      const fillWidth = 20 + pct * 0.80;
      this.progressFill.style.width = Math.min(fillWidth, 100) + '%';
    }
  }

  // Smooth scroll handler for anchor links with sticky header offset
  function initSmoothAnchorScroll() {
    document.querySelectorAll('a[href^="/#"], a[href^="#"]').forEach((link) => {
      link.addEventListener('click', function (e) {
        const href = this.getAttribute('href');
        const hash = href.includes('#') ? href.substring(href.indexOf('#')) : '';
        if (hash && hash !== '#') {
          const target = document.querySelector(hash);
          if (target && (window.location.pathname === '/' || href.startsWith('#'))) {
            e.preventDefault();
            const headerHeight = document.querySelector('.custom-header')?.offsetHeight || 75;
            const targetPos = target.getBoundingClientRect().top + window.pageYOffset - headerHeight;
            window.scrollTo({ top: targetPos, behavior: 'smooth' });
          }
        }
      });
    });
  }

  /* ----------------------------------------------------------
     MOBILE MENU DRAWER
     ---------------------------------------------------------- */
  function initMobileMenu() {
    const openBtn = document.querySelector('[data-mobile-menu-open]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (!openBtn || !menu) return;

    const closeBtn = menu.querySelector('[data-mobile-menu-close]');
    const overlay = menu.querySelector('.custom-header__mobile-overlay');

    function open() {
      menu.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      menu.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    openBtn.addEventListener('click', open);
    closeBtn && closeBtn.addEventListener('click', close);
    overlay && overlay.addEventListener('click', close);
  }

  /* ----------------------------------------------------------
     ADD TO CART (AJAX)
     ---------------------------------------------------------- */
  function initAddToCartButtons() {
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-custom-atc]');
      if (!btn) return;

      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      let qty = 1;
      const qtyInput = document.querySelector('[data-qty-input]');
      if (qtyInput && btn.closest('.custom-pdp__actions')) {
        qty = parseInt(qtyInput.value, 10) || 1;
      }

      const originalText = btn.innerHTML;
      btn.textContent = 'Adding…';
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty }] })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        })
        .then(function () {
          btn.textContent = 'Added ✓';
          setTimeout(function () {
            btn.innerHTML = originalText;
            btn.disabled = false;
          }, 1200);

          // Open the Slide-Over Cart Drawer
          CartDrawer.open();
        })
        .catch(function () {
          btn.textContent = 'Error';
          setTimeout(function () {
            btn.innerHTML = originalText;
            btn.disabled = false;
          }, 1500);
        });
    });
  }

  /* ----------------------------------------------------------
     INIT ON DOM READY
     ---------------------------------------------------------- */
  function init() {
    // Account Dropdown
    AccountDropdown.init();

    // Cart Drawer
    CartDrawer.init();

    // Carousels
    document.querySelectorAll('[data-custom-carousel]').forEach(function (el) {
      new CustomCarousel(el);
    });

    // Smooth anchor scroll
    initSmoothAnchorScroll();

    // Mobile menu
    initMobileMenu();

    // AJAX Add to cart
    initAddToCartButtons();

    // PDP Gallery Thumbnails
    document.querySelectorAll('[data-pdp-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var mainImg = document.querySelector('[data-pdp-main-image]');
        var targetSrc = this.dataset.imageSrc;
        if (mainImg && targetSrc) {
          mainImg.src = targetSrc;
          document.querySelectorAll('[data-pdp-thumb]').forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
        }
      });
    });

    // PDP Quantity Selectors
    document.querySelectorAll('[data-qty-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var input = this.parentElement.querySelector('[data-qty-input]');
        if (!input) return;
        var current = parseInt(input.value, 10) || 1;
        var change = parseInt(this.dataset.qtyBtn, 10);
        var newVal = Math.max(1, current + change);
        input.value = newVal;
      });
    });

    // PDP Buy Now Button
    document.querySelectorAll('[data-buy-now]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var variantId = this.dataset.variantId;
        if (!variantId) return;
        var qtyInput = document.querySelector('[data-qty-input]');
        var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
        window.location.href = '/cart/' + variantId + ':' + qty;
      });
    });

    // Bundle Add Both to Cart
    document.querySelectorAll('[data-add-bundle]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var mainId = parseInt(this.dataset.mainId, 10);
        var addonId = parseInt(this.dataset.addonId, 10);
        if (!mainId) return;

        var items = [{ id: mainId, quantity: 1 }];
        if (addonId) items.push({ id: addonId, quantity: 1 });

        var origText = btn.innerHTML;
        btn.textContent = 'Adding Bundle…';
        btn.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (res) {
            if (!res.ok) throw new Error('Bundle add failed');
            return res.json();
          })
          .then(function () {
            btn.textContent = 'Bundle Added ✓';
            setTimeout(function () {
              btn.innerHTML = origText;
              btn.disabled = false;
            }, 1200);

            // Open the Slide-Over Cart Drawer
            CartDrawer.open();
          })
          .catch(function () {
            btn.textContent = 'Error';
            setTimeout(function () {
              btn.innerHTML = origText;
              btn.disabled = false;
            }, 1500);
          });
      });
    });

    // Dynamic Delivery Date calculation
    var deliveryEl = document.querySelector('[data-dynamic-delivery]');
    if (deliveryEl) {
      var days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      var today = new Date();
      var d1 = new Date(today);
      d1.setDate(today.getDate() + 3);
      var d2 = new Date(today);
      d2.setDate(today.getDate() + 5);
      deliveryEl.textContent = days[d1.getDay()] + ', ' + d1.getDate() + ' ' + months[d1.getMonth()] + ' – ' + days[d2.getDay()] + ', ' + d2.getDate() + ' ' + months[d2.getMonth()];
    }

    // Sticky Buy Bar scroll listener
    var stickyBuy = document.querySelector('[data-sticky-buy]');
    if (stickyBuy) {
      window.addEventListener('scroll', function () {
        if (window.scrollY > 400) {
          stickyBuy.classList.add('is-visible');
        } else {
          stickyBuy.classList.remove('is-visible');
        }
      }, { passive: true });
    }

    // Video Demonstrator Modal
    var videoTrigger = document.querySelector('[data-video-trigger]');
    var videoModal = document.querySelector('[data-video-modal]');
    if (videoTrigger && videoModal) {
      videoTrigger.addEventListener('click', function () {
        videoModal.classList.add('is-open');
      });
      document.querySelectorAll('[data-video-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          videoModal.classList.remove('is-open');
        });
      });
      videoModal.addEventListener('click', function (e) {
        if (e.target === videoModal) {
          videoModal.classList.remove('is-open');
        }
      });
    }

    // Collection Category Filter Pills
    var urlParams = new URLSearchParams(window.location.search);
    var activeCat = urlParams.get('cat');
    var catTitleMap = {
      'toddler': '🧸 Toddler & Rattles',
      'blocks': '🧱 Building Blocks',
      'push-pull': '🚲 Push & Pull',
      'montessori': '🧩 Montessori & Puzzles',
      'stem': '🧮 Learning & STEM',
      'pretend-play': '🎭 Pretend Play',
      'music': '🎵 Music & Sound',
      'other': '🪵 Other Wooden Toys',
      'all': 'All Wooden Toys'
    };

    if (activeCat) {
      var matchingPill = document.querySelector('.custom-filter-pill[data-cat="' + activeCat + '"]');
      if (matchingPill) {
        document.querySelectorAll('.custom-filter-pill').forEach(function (p) { p.classList.remove('is-active'); });
        matchingPill.classList.add('is-active');
      }
      if (catTitleMap[activeCat]) {
        var headingEl = document.querySelector('[data-collection-heading]');
        var breadcrumbEl = document.querySelector('[data-collection-breadcrumb]');
        if (headingEl) headingEl.textContent = catTitleMap[activeCat];
        if (breadcrumbEl) breadcrumbEl.textContent = catTitleMap[activeCat];
      }
    }

    document.querySelectorAll('.custom-filter-pill').forEach(function (pill) {
      pill.addEventListener('click', function (e) {
        document.querySelectorAll('.custom-filter-pill').forEach(function (p) { p.classList.remove('is-active'); });
        this.classList.add('is-active');
        var cat = this.getAttribute('data-cat');
        if (cat && catTitleMap[cat]) {
          var headingEl = document.querySelector('[data-collection-heading]');
          var breadcrumbEl = document.querySelector('[data-collection-breadcrumb]');
          if (headingEl) headingEl.textContent = catTitleMap[cat];
          if (breadcrumbEl) breadcrumbEl.textContent = catTitleMap[cat];
        }
      });
    });

    // Hero Carousel Controller
    var heroCarousel = document.querySelector('[data-hero-carousel]');
    if (heroCarousel) {
      var slides = heroCarousel.querySelectorAll('[data-hero-slide]');
      var dots = heroCarousel.querySelectorAll('[data-hero-dot]');
      var currentIdx = 0;
      var totalSlides = slides.length;
      var autoPlayTimer = null;

      function goToSlide(idx) {
        if (totalSlides <= 1) return;
        currentIdx = (idx + totalSlides) % totalSlides;
        slides.forEach(function (slide, i) {
          if (i === currentIdx) {
            slide.classList.add('is-active');
          } else {
            slide.classList.remove('is-active');
          }
        });
        dots.forEach(function (dot, i) {
          if (i === currentIdx) {
            dot.classList.add('is-active');
          } else {
            dot.classList.remove('is-active');
          }
        });
      }

      function startAutoPlay() {
        stopAutoPlay();
        if (totalSlides > 1) {
          autoPlayTimer = setInterval(function () {
            goToSlide(currentIdx + 1);
          }, 3500);
        }
      }

      function stopAutoPlay() {
        if (autoPlayTimer) {
          clearInterval(autoPlayTimer);
          autoPlayTimer = null;
        }
      }

      dots.forEach(function (dot) {
        dot.addEventListener('click', function (e) {
          e.preventDefault();
          var targetIdx = parseInt(this.dataset.heroDot, 10);
          goToSlide(targetIdx);
          startAutoPlay();
        });
      });

      heroCarousel.addEventListener('mouseenter', stopAutoPlay);
      heroCarousel.addEventListener('mouseleave', startAutoPlay);

      // Touch swipe support
      var startX = 0;
      heroCarousel.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
        stopAutoPlay();
      }, { passive: true });

      heroCarousel.addEventListener('touchend', function (e) {
        var diffX = e.changedTouches[0].clientX - startX;
        if (Math.abs(diffX) > 40) {
          if (diffX < 0) {
            goToSlide(currentIdx + 1);
          } else {
            goToSlide(currentIdx - 1);
          }
        }
        startAutoPlay();
      }, { passive: true });

      startAutoPlay();
    }

    // Dynamic Product Detail Page Parser (Highlights & Developmental Benefits)
    var descEl = document.querySelector('[data-pdp-desc-content]');
    if (descEl) {
      // 1. Parse Developmental Benefits from Description
      var headers = Array.from(descEl.querySelectorAll('h3, h4, strong'));
      var benefitsHeader = headers.find(function (h) {
        return /Developmental|Learning Benefit|Benefit/i.test(h.textContent);
      });

      if (benefitsHeader) {
        var ulBenefits = benefitsHeader.closest('li') ? null : (benefitsHeader.nextElementSibling || (benefitsHeader.parentElement ? benefitsHeader.parentElement.nextElementSibling : null));
        while (ulBenefits && ulBenefits.tagName !== 'UL' && ulBenefits.nextElementSibling) {
          ulBenefits = ulBenefits.nextElementSibling;
        }
        if (ulBenefits && ulBenefits.tagName === 'UL') {
          var benefitItems = Array.from(ulBenefits.querySelectorAll('li')).map(function (li) {
            return li.textContent.replace(/^[✓•\s-]+/, '').trim();
          }).filter(Boolean);

          if (benefitItems.length >= 2) {
            var benefitsGrid = document.querySelector('[data-pdp-benefits-grid]');
            if (benefitsGrid) {
              benefitsGrid.innerHTML = benefitItems.slice(0, 4).map(function (b) {
                return '<div class="custom-pdp__benefit-pill">✓ ' + b + '</div>';
              }).join('');
            }
          }
        }
      }

      // 2. Parse Key Highlights / Features from Description
      var highlightsHeader = headers.find(function (h) {
        return /Key Highlight|Feature|Highlight/i.test(h.textContent);
      });

      if (highlightsHeader) {
        var ulHighlights = highlightsHeader.closest('li') ? null : (highlightsHeader.nextElementSibling || (highlightsHeader.parentElement ? highlightsHeader.parentElement.nextElementSibling : null));
        while (ulHighlights && ulHighlights.tagName !== 'UL' && ulHighlights.nextElementSibling) {
          ulHighlights = ulHighlights.nextElementSibling;
        }
        if (ulHighlights && ulHighlights.tagName === 'UL') {
          var featureItems = Array.from(ulHighlights.querySelectorAll('li')).map(function (li) {
            var strong = li.querySelector('strong, b');
            var title = strong ? strong.textContent.replace(/[:\s]+$/, '').trim() : '';
            var text = strong ? li.textContent.replace(strong.textContent, '').replace(/^[:\s-]+/, '').trim() : li.textContent.trim();
            return { title: title || 'Key Highlight', desc: text };
          }).filter(function (f) { return f.desc || f.title; });

          if (featureItems.length >= 2) {
            var specsGrid = document.querySelector('[data-pdp-specs-grid]');
            if (specsGrid) {
              var thumbs = Array.from(document.querySelectorAll('.custom-pdp__thumb img')).map(function (img) {
                return img.src.replace(/width=\d+/, 'width=600');
              });
              var mainImg = document.querySelector('.custom-pdp__main-image');
              var defaultImg = mainImg ? mainImg.src : '';

              var cardsHtml = '';
              featureItems.slice(0, 3).forEach(function (item, idx) {
                var imgSrc = thumbs[idx + 1] || thumbs[idx] || defaultImg;
                cardsHtml += `
                  <div class="custom-pdp__spec-card">
                    <div class="custom-pdp__spec-img-wrap">
                      <img src="${imgSrc}" alt="${item.title}" loading="lazy" width="400" height="280">
                    </div>
                    <div class="custom-pdp__spec-text">
                      <h4>${item.title}</h4>
                      <p>${item.desc}</p>
                    </div>
                  </div>
                `;
              });

              // 4th Card: Quality & Safety Spec Card
              cardsHtml += `
                <div class="custom-pdp__spec-card custom-pdp__spec-card--dimensions">
                  <div class="custom-pdp__spec-dimension-box">
                    <div class="custom-pdp__dimension-diagram">
                      <span class="custom-pdp__dimension-line">BIS 🛡️</span>
                      <div class="custom-pdp__dimension-checklist">
                        <div><span style="color: #FF7A00;">✓</span> 100% Solid Natural Wood</div>
                        <div><span style="color: #FF7A00;">✓</span> Child-Safe Water Colors</div>
                        <div><span style="color: #FF7A00;">✓</span> Smooth Splinter-Free Edges</div>
                        <div><span style="color: #FF7A00;">✓</span> BIS Certified Safe</div>
                      </div>
                    </div>
                  </div>
                  <div class="custom-pdp__spec-text">
                    <h4>Certified Quality & Safety</h4>
                    <p>Meticulously polished by hand with 100% non-toxic finishes and smooth rounded corners, ensuring safe, screen-free playtime.</p>
                  </div>
                </div>
              `;

              specsGrid.innerHTML = cardsHtml;
            }
          }
        }
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section render (theme editor)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      init();
    });
  }
})();
